import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { ExamService, ExamInformation, ExamFilters } from '../../services/exam.service';

@Component({
    selector: 'app-exam-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgbPaginationModule,
        NgbTooltipModule,
        NgbAlertModule,
        NgbDropdownModule,
        StaffHeaderComponent,
        StaffNavComponent
    ],
    templateUrl: './exam-management.component.html',
    styleUrl: './exam-management.component.scss'
})
export class ExamManagementComponent implements OnInit, OnDestroy {
    currentUser: AuthUser | null = null;
    isMobileMenuOpen = false;
    private userSubscription: Subscription | undefined;

    exams: ExamInformation[] = [];
    filteredExams: ExamInformation[] = [];
    isLoading = false;
    error: string | null = null;
    showMockDataWarning = false;

    filters: ExamFilters = {
        searchTerm: '',
        category: '',
        level: '',
        status: '',
        instructor: ''
    };

    page = 1;
    pageSize = 10;
    readonly pageSizeOptions = [10, 20, 50, 100];
    collectionSize = 0;

    sortField = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    private subscription = new Subscription();

    constructor(
        private examService: ExamService,
        private authService: AuthService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) { }

    ngOnInit(): void {
        console.log('🔄 ExamManagementComponent ngOnInit เริ่มต้น');

        this.userSubscription = this.authService.currentUser$.subscribe(user => {
            console.log('👤 Current user updated:', user);
            this.currentUser = user;
            this.cdr.detectChanges();
        });

        this.ngZone.run(() => {
            setTimeout(() => {
                console.log('⏰ เริ่มโหลดข้อมูลการสอบหลังจาก timeout (inside NgZone)');
                this.loadExams();
            }, 100);
        });
    }

    ngOnDestroy(): void {
        console.log('🗑️ ExamManagementComponent ngOnDestroy');
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
        this.subscription.unsubscribe();
    }

    onLogout(): void {
        this.authService.logout();
    }

    toggleMobileMenu(): void {
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpen = false;
    }

    onNavigate(route: string): void {
        this.closeMobileMenu();

        switch (route) {
            case 'users':
                this.router.navigate(['/staff/user-management']);
                break;
            case 'courses':
                this.router.navigate(['/staff/course-management']);
                break;
            case 'exams':
                break;
            case 'reports':
                this.router.navigate(['/staff/dashboard']);
                break;
            case 'settings':
                this.router.navigate(['/staff/dashboard']);
                break;
            default:
                this.router.navigate(['/staff/dashboard']);
                break;
        }
    }

    loadExams(): void {
        console.log('🚀 เริ่มโหลดข้อมูลการสอบ...');

        this.isLoading = true;
        this.error = null;

        const sub = this.examService.getAllExams().subscribe({
            next: (exams: ExamInformation[]) => {
                this.ngZone.run(() => {
                    console.log('✅ โหลดข้อมูลการสอบสำเร็จ:', exams.length, 'รายการ');
                    console.log('📊 ข้อมูลการสอบที่ได้รับ:', exams);

                    if (exams && Array.isArray(exams)) {
                        this.exams = [...exams];
                        this.filteredExams = [...this.exams];
                        this.collectionSize = this.filteredExams.length;

                        if (exams.length > 0 && exams[0].id === '1') {
                            this.showMockDataWarning = true;
                        }
                    } else {
                        console.warn('⚠️ ข้อมูลการสอบที่ได้รับไม่ถูกต้อง:', exams);
                        this.exams = [];
                        this.filteredExams = [];
                        this.collectionSize = 0;
                    }

                    this.isLoading = false;
                    this.cdr.detectChanges();
                });
            },
            error: (error: any) => {
                this.ngZone.run(() => {
                    console.error('❌ เกิดข้อผิดพลาดในการโหลดการสอบ:', error);
                    this.error = error;
                    this.exams = [];
                    this.filteredExams = [];
                    this.collectionSize = 0;
                    this.isLoading = false;
                    this.cdr.detectChanges();
                });
            }
        });

        this.subscription.add(sub);
    }

    applyFilters(): void {
        console.log('🔍 กำลังใช้ตัวกรอง:', this.filters);

        this.page = 1;

        this.filteredExams = this.exams.filter(exam => {
            if (this.filters.searchTerm) {
                const searchTerm = this.filters.searchTerm.toLowerCase();
                const titleMatch = exam.examTitle?.toLowerCase().includes(searchTerm);
                const remarksMatch = exam.remarks?.toLowerCase().includes(searchTerm);
                const locationMatch = exam.examLocation?.toLowerCase().includes(searchTerm);

                if (!titleMatch && !remarksMatch && !locationMatch) {
                    return false;
                }
            }

            if (this.filters.level && exam.valuerRank !== this.filters.level) {
                return false;
            }

            if (this.filters.status && exam.examStatus?.name !== this.filters.status) {
                return false;
            }

            return true;
        });

        this.collectionSize = this.filteredExams.length;

        if (this.sortField) {
            this.sortExams();
        }
    }

    clearFilters(): void {
        console.log('🔄 ล้างตัวกรองทั้งหมด');

        this.filters = {
            searchTerm: '',
            category: '',
            level: '',
            status: '',
            instructor: ''
        };

        this.page = 1;
        this.filteredExams = [...this.exams];
        this.collectionSize = this.filteredExams.length;
    }


    /**

     * เปลี่ยนจำนวนรายการต่อหน้า

     */

    changePageSize(size: number): void {

      this.pageSize = size;

      this.page = 1;

    }

    onPageChange(newPage: number): void {
        console.log('📄 เปลี่ยนหน้า:', newPage);
        this.page = newPage;
    }

    onSort(field: string): void {
        console.log('🔄 จัดเรียงตาม:', field);

        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }

        this.sortExams();
    }

    private sortExams(): void {
        this.filteredExams.sort((a, b) => {
            let valueA: any, valueB: any;

            switch (this.sortField) {
                case 'examTitle':
                    valueA = a.examTitle || '';
                    valueB = b.examTitle || '';
                    break;
                case 'valuerRank':
                    valueA = a.valuerRank || '';
                    valueB = b.valuerRank || '';
                    break;
                case 'examLocation':
                    valueA = a.examLocation || '';
                    valueB = b.examLocation || '';
                    break;
                case 'status':
                    valueA = a.examStatus?.name || '';
                    valueB = b.examStatus?.name || '';
                    break;
                case 'examDate':
                    valueA = new Date(a.examDate || 0);
                    valueB = new Date(b.examDate || 0);
                    break;
                case 'registrationDeadline':
                    valueA = new Date(a.registrationDeadline || 0);
                    valueB = new Date(b.registrationDeadline || 0);
                    break;
                case 'examFee':
                    valueA = parseFloat(a.examFee || '0');
                    valueB = parseFloat(b.examFee || '0');
                    break;
                default:
                    return 0;
            }

            let comparison = 0;
            if (valueA > valueB) {
                comparison = 1;
            } else if (valueA < valueB) {
                comparison = -1;
            }

            return this.sortDirection === 'desc' ? -comparison : comparison;
        });
    }

    getSortIcon(field: string): string {
        if (this.sortField !== field) {
            return 'fas fa-sort text-muted';
        }
        return this.sortDirection === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
    }

    viewExam(exam: ExamInformation): void {
        console.log('👁️ ดูรายละเอียดการสอบ:', exam);
        this.router.navigate(['/staff/exam-management/view', exam.id]);
    }

    editExam(exam: ExamInformation): void {
        console.log('✏️ แก้ไขการสอบ:', exam);
        this.router.navigate(['/staff/exam-management/edit', exam.id]);
    }

    deleteExam(exam: ExamInformation): void {
        console.log('🗑️ ลบการสอบ:', exam);
    }

    formatDate(dateString?: string): string {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return '-';
        }
    }

    formatCurrency(amount?: string): string {
        if (!amount) return '-';

        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return '-';

        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(numAmount);
    }

    getStatusBadgeClass(examStatus?: { name: string }): string {
        const statusName = examStatus?.name || '';

        switch (statusName) {
            case 'เตรียมเปิดสอบ':
                return 'badge bg-warning';
            case 'เปิดรับสมัคร':
                return 'badge bg-success';
            case 'ปิดรับสมัคร':
                return 'badge bg-danger';
            case 'สอบเสร็จสิ้น':
                return 'badge bg-secondary';
            default:
                return 'badge bg-secondary';
        }
    }

    getStatusText(examStatus?: { name: string }): string {
        return examStatus?.name || '-';
    }

    getValuerRankBadgeClass(valuerRank?: string): string {
        if (!valuerRank) return 'badge bg-secondary';

        if (valuerRank.includes('1')) {
            return 'badge bg-success';
        } else if (valuerRank.includes('2')) {
            return 'badge bg-info';
        } else if (valuerRank.includes('3')) {
            return 'badge bg-warning';
        } else if (valuerRank.includes('เฉพาะทาง') || valuerRank.includes('extraordinary')) {
            return 'badge bg-danger';
        } else if (valuerRank.includes('ทุกระดับ')) {
            return 'badge bg-primary';
        }

        return 'badge bg-secondary';
    }

    getValuerRankText(valuerRank?: string): string {
        return valuerRank || '-';
    }

    trackByExamId(index: number, exam: ExamInformation): string {
        return exam.id || index.toString();
    }

    getPaginatedExams(): ExamInformation[] {
        const startIndex = (this.page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.filteredExams.slice(startIndex, endIndex);
    }

    onDropdownOpenChange(isOpen: boolean, examId?: string): void {
        if (isOpen) {
            console.log('📂 เปิด dropdown สำหรับการสอบ:', examId);
        }
    }

    Math = Math;

    openCreateExamModal(): void {
        console.log('📝 นำทางไปหน้าสร้างการสอบใหม่');
        this.router.navigate(['/staff/exam-management/create']);
    }

    getRegisteredCount(exam: ExamInformation): number {
        return exam.cpdHours?.length || 0;
    }
}
